import { ReactNode, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router";
import { createIntl, defineMessages, useIntl } from "react-intl";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { IconChevronRight } from "@tabler/icons-react";
import { classNames, useAppConfig } from "@ct-react/core";
import { defaultLocale, libTranslate, useLocaleContext, useLocaleFormatter } from "@ct-react/locale";
import { ArticleType } from "@shared/url";
import { MailContact } from "@shared/graphql";
import { getIntlMessages } from "../i18n";
import { actions, form } from "../i18n/sharable-defs";
import { useTools } from "../hooks/tools";
import { useForm, validators } from "../hooks/form";
import { getEnumKeyByValue } from "../tools/enum";
import { Wrapper } from "../components/layout/wrapper";
import Button from "../components/common/button";
import Select, { Option } from "../components/inputs/select";
import { RangeSliderInput } from "../components/inputs/slider";
import CheckboxInput from "../components/inputs/checkbox";
import PageHero from "../components/layout/page-elements/page-hero";
import Elsje from "../components/common/elsje";
import hero from "../assets/bg-images/hero-alert.webp";
import "./mail-registration.scss";

const transDefs = defineMessages({
  title: { id: "alert.title", defaultMessage: "Alerte email" },
  chapo: { id: "alert.chapo", defaultMessage: "Vous êtes à la recherche d'un bien immobilier ? Merci de compléter le formulaire" },
  success: { id: "alert.form.success", defaultMessage: "Votre inscription a été prise en compte avec succès" },
  articleType: { id: "alert.form.article-type-label", defaultMessage: "Type de transaction" },
  articleTypePlaceholder: { id: "alert.form.article-type-placeholder", defaultMessage: "Sélectionnez un type de transaction" },
  sale: { id: "alert.form.article-type-sale", defaultMessage: "Vente" },
  rental: { id: "alert.form.article-type-rental", defaultMessage: "Location" },
  zip: { id: "alert.form.zip", defaultMessage: "Ville" },
  zipPlaceholder: { id: "alert.form.zip-placeholder", defaultMessage: "Sélectionnez une localité" },
  surface: { id: "alert.form.surface", defaultMessage: "Surface minimum" },
  rooms: { id: "alert.form.rooms", defaultMessage: "Nombre de pièces minimum" },
  budget: { id: "alert.form.budget", defaultMessage: "Budget" }
});

const LOCATIONS_GQL_DATA = gql`
  query AlertLocations {
    cities: searchableAccommodationCities { zip city }
  }
`;

const SEND_MAIL_TO_AGENCY_GQL_MUT = gql`
  mutation MailAlert($agencyId: ID!, $request: MailRequestInput!) {
    mailRelatedContact(merchantId: $agencyId, request: $request) @recaptcha(action: "mail_form")
  }
`;

const allowedObjectTypes = [ 43, 4, 93, 48 ];

type FormData = {
  contact: MailContact;
  data: {
    articleType: ArticleType;
    objectType: number;
    zip: string;
    surface?: number;
    rooms?: number;
    minPrice?: number;
    maxPrice?: number;
  }
  legal: boolean
}

const MailRegistrationPage = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { options: { agencyId } } = useAppConfig();
  const { transLib, print } = useLocaleFormatter();
  const { state } = useLocation();
  const { mutation } = useTools();

  const budgetMax = useRef<number>(3000000);
  const { register, formState, patch, handleSubmit } = useForm<FormData>({ legal: false, data: { articleType: ArticleType.SALE } });
  const policyPath = useMemo(() => `/conditions/policy-${locale.basename.toLowerCase()}.pdf`, [ locale ]);

  const { data: locationData, loading } = useQuery<any>(LOCATIONS_GQL_DATA);
  const [ sendMail ] = useMutation(SEND_MAIL_TO_AGENCY_GQL_MUT);

  useEffect(() => {
    if (!state || loading) return;
    // warning: todo doesn't handle multi-select for now
    const { data, ...rest } = state;
    const { objectTypes, zips, ...dataRest } = data || {};
    patch(false, {
      ...rest,
      data: {
        ...dataRest,
        ...(!!objectTypes) && { objectType: objectTypes[0], },
        ...(!!zips) && { zip: zips[0], }
      }
    } as FormData);
  }, [ state, loading ]);

  const labelize = useCallback((amount: number) => {
    const price = print.price({ amount, currency: "CHF" }, {
      trailingZeroDisplay: "stripIfInteger"
    });
    return `${price}${amount === budgetMax.current ? "+" : ""}`;
  }, [ intl.locale ]);

  const onBudgetChange = (range?: [ number, number ]) => {
    const data = !!range ? { minPrice: range[0], maxPrice: range[1] } : {};
    patch(false, { data });
  }

  const submit = async (data: Partial<FormData>) => {
    const fr = createIntl({ locale: defaultLocale.code, messages: getIntlMessages(defaultLocale) });
    //@ts-ignore
    const articleType = fr.formatMessage(transDefs[getEnumKeyByValue(ArticleType, data.data!.articleType)!.toLowerCase()]);
    const objectTypes = libTranslate(fr, "objectTypes", +data.data!.objectType);
    const city = locationData.cities.find(({ zip }: any) => zip === data.data!.zip).city || "indéfini";
    const surface = !!data.data!.surface && +data.data!.surface > 0 ? `${data.data!.surface} m²` : "indéfini";
    const rooms = !!data.data!.rooms && +data.data!.rooms > 0 ? data.data!.rooms : "indéfini";
    const minPrice = !!data.data!.minPrice && +data.data!.minPrice > 0 ? `${data.data!.minPrice} CHF` : "indéfini";
    const maxPrice = !!data.data!.maxPrice && +data.data!.maxPrice > 0 ? `${data.data!.maxPrice} CHF` : "indéfini";
    const variables = {
      agencyId,
      request: {
        contact: data.contact,
        subject: "Inscription - alerte email",
        body: `Paramètres de configuration de l'alerte:\n\nType de transaction: ${articleType}\nType de bien: ${objectTypes}\nVille: ${city}\nSurface minimum: ${surface}\nNombre de pièces: ${rooms}\nPrix minimum: ${minPrice}\nPrix maximum: ${maxPrice}\n`
      }
    };
    await mutation.withRecaptcha(sendMail, "mail_form")({ variables })
                  .then(() => patch(true));
  }

  return (
    <>
      <meta name="robots" content="noindex" />

      <PageHero background={hero}
                title={intl.formatMessage(transDefs.title)}
                chapo={intl.formatMessage(transDefs.chapo)} />

      <Wrapper.Centered className="content register">

        <main>
          <form className="register-form"
                onSubmit={handleSubmit(submit, { resetStateSubmitTimer: 5000 })}>

            <div className="f-group">
              <label htmlFor="firstName">{`${intl.formatMessage(form.firstName)}*`}</label>
              <input type="text" id="firstName"
                     className={classNames({ dirty: "contact.firstName" in formState.dirtyFields })}
                     {...register("contact.firstName", { required: intl.formatMessage(form.emptyError) })}
                     placeholder={intl.formatMessage(form.firstName)}
                     autoComplete="given-name" />
              <p className="f-error">
                {!!formState.dirtyFields["contact.firstName"] && formState.dirtyFields["contact.firstName"].error}
              </p>
            </div>

            <div className="f-group">
              <label htmlFor="lastName">{`${intl.formatMessage(form.lastName)}*`}</label>
              <input type="text" id="lastName"
                     className={classNames({ dirty: "contact.lastName" in formState.dirtyFields })}
                     {...register("contact.lastName", { required: intl.formatMessage(form.emptyError) })}
                     placeholder={intl.formatMessage(form.lastName)}
                     autoComplete="family-name" />
              <p className="f-error">
                {!!formState.dirtyFields["contact.lastName"] && formState.dirtyFields["contact.lastName"].error}
              </p>
            </div>

            <div className="f-group">
              <label htmlFor="email">{`${intl.formatMessage(form.email)}*`}</label>
              <input type="email" id="email"
                     className={classNames({ dirty: "contact.email" in formState.dirtyFields })}
                     {...register("contact.email", {
                       required: intl.formatMessage(form.emptyError),
                       validators: { ...validators.emailFormat(intl.formatMessage(form.emailError)) }
                     })}
                     placeholder={intl.formatMessage(form.email)}
                     autoComplete="email" />
              <p className="f-error">
                {!!formState.dirtyFields["contact.email"] && formState.dirtyFields["contact.email"].error}
              </p>
            </div>

            <div className="f-group">
              <label htmlFor="phone">{`${intl.formatMessage(form.phone)}*`}</label>
              <input type="tel" id="phone"
                     className={classNames({ dirty: "contact.phone" in formState.dirtyFields })}
                     {...register("contact.phone", {
                       required: intl.formatMessage(form.emptyError),
                       validators: { ...validators.phoneFormat(intl.formatMessage(form.phoneError)) }
                     })}
                     placeholder={intl.formatMessage(form.phone)}
                     autoComplete="tel" />
              <p className="f-error">
                {!!formState.dirtyFields["contact.phone"] && formState.dirtyFields["contact.phone"].error}
              </p>
            </div>

            <div className="f-group">
              <label>{`${intl.formatMessage(transDefs.articleType)}*`}</label>
              <Select className={classNames({ dirty: "data.articleType" in formState.dirtyFields })}
                      placeholder={intl.formatMessage(form.objectTypePlaceholder)}
                      {...register("data.articleType", { readOnly: true, required: intl.formatMessage(form.emptyError) })}>
                {Object.entries(ArticleType).map(([ type, name ]) =>
                  //@ts-ignore
                  <Option key={type} value={name} label={intl.formatMessage(transDefs[type.toLowerCase()])}/>
                )}
              </Select>
              <p className="f-error">
                {!!formState.dirtyFields["data.articleType"] && formState.dirtyFields["data.articleType"].error}
              </p>
            </div>

            <div className="f-group">
              <label htmlFor="objectType">{`${intl.formatMessage(form.objectType)}*`}</label>
              <Select className={classNames({ dirty: "data.objectType" in formState.dirtyFields })}
                      placeholder={intl.formatMessage(form.objectTypePlaceholder)}
                      {...register("data.objectType", { required: intl.formatMessage(form.emptyError) })}>
                {allowedObjectTypes.map(id =>
                  <Option key={id} value={id} label={transLib.match("objectTypes", id)} />
                )}
              </Select>
              <p className="f-error">
                {!!formState.dirtyFields["data.objectType"] && formState.dirtyFields["data.objectType"].error}
              </p>
            </div>

            <div className="f-group">
              <label>{`${intl.formatMessage(transDefs.zip)}*`}</label>
              <Select className={classNames({ dirty: "data.zip" in formState.dirtyFields })}
                      placeholder={intl.formatMessage(transDefs.zipPlaceholder)}
                      {...register("data.zip", { required: intl.formatMessage(form.emptyError) })}>
                  {(locationData?.cities || []).map((city: any, index: number) =>
                    <Option key={index} value={city.zip} label={city.city} />
                  )}
                  <Option value="any" label={intl.formatMessage(form.other)} />
              </Select>
              <p className="f-error">
                {!!formState.dirtyFields["data.zip"] && formState.dirtyFields["data.zip"].error}
              </p>
            </div>

            <div className="f-group no-error">
              <label htmlFor="surface">{intl.formatMessage(transDefs.surface)}</label>
              <input type="number" id="surface" min={0}
                     {...register("data.surface")}
                     placeholder="Min. m²" />
            </div>

            <div className="f-group no-error">
              <label htmlFor="rooms">{intl.formatMessage(transDefs.rooms)}</label>
              <input type="number" id="rooms" min={0}
                     {...register("data.rooms")}
                     placeholder="Min." />
            </div>

            <div className="f-group no-error budget">
              <label>{intl.formatMessage(transDefs.budget)}</label>
              <RangeSliderInput config={{ min: 0, max: budgetMax.current, step: 100000, limitsAsUndefined: true, labelize }}
                                onChange={onBudgetChange} />
            </div>

            <div className="full f-group no-error">
              {/*@ts-ignore*/}
              <CheckboxInput id="legal"
                             label={intl.formatMessage(form.legal, { link: (chunks: ReactNode) =>
                                 (<a key="link" href={policyPath} target="_blank">{chunks}</a>)}
                             )}
                             {...register<boolean>("legal", { required: intl.formatMessage(form.emptyError) })} />
            </div>

            <div className="full submitter">
              <Button type="submit" className="cream bold l-spacing with-icon"
                      processing={formState.submit.inProcess}
                      disabled={!formState.isValid}>
                {intl.formatMessage(actions.validate)}
                <IconChevronRight />
              </Button>
              {formState.submit.finished &&
                <span className={classNames("callback", { error: !formState.submit.succeeded })}>
                  {intl.formatMessage(formState.submit.succeeded ? transDefs.success : form.failed)}
                </span>
              }
            </div>

          </form>
        </main>

        <aside><Elsje /></aside>

      </Wrapper.Centered>
    </>);
}

export default MailRegistrationPage;
